/**
 * THE NO-SHOW RECOVERY DESK (MISSED-APPOINTMENT-RECOVERY-V1) -- the COC surface
 * for Partha SLA item 5: when a patient misses an appointment, the clinic sees
 * it in a REAL recovery queue with the lawful next step, drilling to the record
 * without leaving the list (the 360 law).
 *
 * READ-ONLY: this desk OBSERVES the EXISTING no_show facts (the writer at
 * markAppointmentNoShow) -- there is no writer here. Each row shows WHO (the
 * patient), WHEN (the missed slot + its age), WHICH CLINIC, and the lawful NEXT
 * STEP; "Open" drills to the canonical Lead 360 quick-view (leadId) without
 * losing the list. The "notify the assigned agent" half is the NAMED
 * notification gap (NOTIFICATION-SEAM-V1, V4-P0n) -- never faked here.
 *
 * Honest states (never a fake zero): loading / error (server message verbatim,
 * status-aware) / empty / the capped read surfaced. Timestamps render through
 * the ONE IST formatter. Zero clinical fields, zero contact channels on this
 * wire (server-carved). Reuses the shipped record grammar (PageHeader,
 * breadcrumbs, the .coc-station table family, LeadQuickViewHost) -- zero new
 * primitives.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { clinicLabelOf } from '@/dashboard/clinic-context';
import { Link } from 'react-router-dom';
import {
  fetchNoShowRecovery,
  type NoShowRecoveryRowView,
  type NoShowRecoveryView,
} from '@/dashboard/api';
import DashboardLoadError from '@/dashboard/DashboardLoadError';
import LeadQuickViewHost from '@/dashboard/leads/LeadQuickViewHost';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { PageHeader } from '@/dashboard/ui';
import { formatIstDateTime } from '@/lib/datetime-ist';

export type NoShowLoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string; status?: number }
  | { kind: 'ready'; view: NoShowRecoveryView };

type DrawerState = { kind: 'none' } | { kind: 'quick_view'; row: NoShowRecoveryRowView };

export interface NoShowRecoveryPageProps {
  /** TEST-ONLY state injection (react-dom/server renders cannot run effects);
   *  production callers render with no props and fetch live. */
  readonly initialState?: NoShowLoadState;
}

/** Compact whole-unit age label (mirrors the manager glance formatAge so the
 *  COC desk reads the SAME way). Null -> '-'. */
function formatAge(minutes: number | null): string {
  if (minutes === null) return '-';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/** The row's display name: the golden/booking name, else an honest ref. */
function rowName(row: NoShowRecoveryRowView): string {
  if (row.patientLabel !== null && row.patientLabel.trim() !== '') {
    return row.patientLabel;
  }
  return row.leadId !== null ? `Lead #${row.leadId}` : `Appointment #${row.appointmentId}`;
}

export default function NoShowRecoveryPage({
  initialState,
}: NoShowRecoveryPageProps = {}) {
  const [state, setState] = useState<NoShowLoadState>(
    initialState ?? { kind: 'loading' },
  );
  const [clinicFilter, setClinicFilter] = useState<number | 'all'>('all');
  const [drawer, setDrawer] = useState<DrawerState>({ kind: 'none' });
  // FRONT-DESK-DAY-BOARD-V1: a config-admitted caller OUTSIDE the coc nav
  // section (a granted front desk, via RequireNoShowQueue) must not be
  // handed the COC breadcrumb -- that door bounces for them (LES-016).
  // Fail-same: no resolved /me (tests, boot) keeps the historical COC
  // chrome byte-identically; only a RESOLVED non-coc caller gets the
  // role-neutral header. Chrome only -- the queue read and every row
  // render are identical for every admitted caller.
  const meCtx = useDashboardMeContext();
  const cocChrome =
    meCtx?.me == null ? true : meCtx.me.navSections.includes('coc');

  const load = useCallback(() => {
    setState({ kind: 'loading' });
    void fetchNoShowRecovery()
      .then((view) => setState({ kind: 'ready', view }))
      .catch((e: unknown) => {
        const status =
          typeof e === 'object' && e !== null && 'status' in e
            ? Number((e as { status: unknown }).status)
            : undefined;
        setState({
          kind: 'error',
          message: e instanceof Error ? e.message : String(e),
          ...(status !== undefined && Number.isFinite(status) ? { status } : {}),
        });
      });
  }, []);

  useEffect(() => {
    if (initialState !== undefined) return;
    load();
  }, [initialState, load]);

  // Distinct clinics WITH misses (for the client-side filter) -- derived from
  // the SAME rows, oldest label wins; only rendered when there is more than one.
  const clinics = useMemo(() => {
    if (state.kind !== 'ready') return [];
    const byId = new Map<number, string | null>();
    for (const row of state.view.rows) {
      if (!byId.has(row.clinicId)) byId.set(row.clinicId, row.clinicLabel);
    }
    return [...byId.entries()].map(([clinicId, clinicLabel]) => ({ clinicId, clinicLabel }));
  }, [state]);

  if (state.kind === 'loading') {
    return (
      <section className="coc-station" data-testid="no-show-loading">
        <div className="loading">Loading the no-show recovery queue...</div>
      </section>
    );
  }
  if (state.kind === 'error') {
    return (
      <section className="coc-station" data-testid="no-show-error">
        <DashboardLoadError
          message={state.message}
          {...(state.status !== undefined ? { status: state.status } : {})}
        />
      </section>
    );
  }

  const { view } = state;
  const rows =
    clinicFilter === 'all'
      ? view.rows
      : view.rows.filter((r) => r.clinicId === clinicFilter);

  return (
    <section className="coc-station" data-testid="no-show-page" aria-label="No-show recovery">
      <PageHeader
        eyebrow={cocChrome ? 'COC' : 'Recovery'}
        title="No-show recovery"
        breadcrumbs={
          cocChrome
            ? [
                { label: 'COC', to: '/dashboard/coc' },
                { label: 'No-show recovery' },
              ]
            : [{ label: 'No-show recovery' }]
        }
        subs={[
          `${view.summary.rowCount} to recover${view.summary.oldestAgeMinutes !== null ? ` - oldest ${formatAge(view.summary.oldestAgeMinutes)}` : ''}`,
          `As of ${formatIstDateTime(view.generatedAtIso)}`,
        ]}
        testId="no-show-header"
      />

      {view.summary.capped ? (
        <p className="panel-caveat" data-testid="no-show-capped">
          Large queue: showing the newest {view.rows.length} missed appointments,
          so the count is a lower bound.
        </p>
      ) : null}

      {clinics.length > 1 ? (
        <div className="coc-station__filters">
          <select
            className="coc-station__clinic-filter"
            value={clinicFilter === 'all' ? 'all' : String(clinicFilter)}
            onChange={(e) =>
              setClinicFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))
            }
            aria-label="Filter by clinic"
            data-testid="no-show-clinic-filter"
          >
            <option value="all">All my clinics</option>
            {clinics.map((c) => (
              <option key={c.clinicId} value={String(c.clinicId)}>
                {clinicLabelOf(c.clinicLabel)}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {rows.length === 0 ? (
        <p className="coc-station__empty" data-testid="no-show-empty">
          No missed appointments to recover right now.
        </p>
      ) : (
        <div className="coc-station__table-wrap">
          <table className="table coc-station__table" data-testid="no-show-table">
            <thead>
              <tr>
                <th scope="col">Patient</th>
                <th scope="col">Clinic</th>
                <th scope="col">Missed appointment (IST)</th>
                <th scope="col">Age</th>
                <th scope="col">Next step</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.appointmentId}
                  data-testid={`no-show-row-${row.appointmentId}`}
                >
                  <td>
                    {row.leadId !== null ? (
                      <button
                        type="button"
                        className="coc-station__name-link"
                        onClick={() => setDrawer({ kind: 'quick_view', row })}
                        data-testid={`no-show-open-${row.appointmentId}`}
                      >
                        {rowName(row)}
                      </button>
                    ) : (
                      <span>{rowName(row)}</span>
                    )}
                  </td>
                  <td>{clinicLabelOf(row.clinicLabel)}</td>
                  <td>{formatIstDateTime(row.appointmentTimeIso)}</td>
                  <td>{formatAge(row.ageMinutes)}</td>
                  <td>{row.nextStepLabel}</td>
                  <td className="coc-station__actions">
                    {row.leadId !== null ? (
                      <button
                        type="button"
                        className="ac-btn ac-btn--secondary ac-btn--sm"
                        onClick={() => setDrawer({ kind: 'quick_view', row })}
                        data-testid={`no-show-open-action-${row.appointmentId}`}
                      >
                        Open
                      </button>
                    ) : (
                      <span className="badge badge--info" title="No lead record linked to this appointment">
                        No linked record
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="coc-station__footnote">
        Full record:{' '}
        <Link to="/dashboard/leads">open the Leads workspace</Link> - the recovery
        desk drills there without losing this queue (opens the same canonical
        Lead 360). Notifying the assigned agent rides the notification seam
        (V4-P0n) once a delivery path exists.
      </p>

      {drawer.kind === 'quick_view' && drawer.row.leadId !== null ? (
        <LeadQuickViewHost
          leadId={drawer.row.leadId}
          seedTitle={rowName(drawer.row)}
          onClose={() => setDrawer({ kind: 'none' })}
          onChanged={load}
        />
      ) : null}
    </section>
  );
}
